import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetContactCustomFieldsApi } from 'src/api/customFields';
import { SelectOption } from 'src/UIKit';
import { ContactMetadataType } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useGroupsApi } from 'src/modules/groups/api';

export type AutomationOptions = {
  groupOptions: SelectOption<string>[];
  metadataOptions: SelectOption<string, string, ContactMetadataType>[];
  isOptionsLoading: boolean;
};

const emptyOptions: AutomationOptions = {
  groupOptions: [],
  metadataOptions: [],
  isOptionsLoading: false,
};

// Loads the group + custom field option lists used by the automation form.
// Fetched once when the automations page mounts so the form dialog opens
// instantly with the data already available.
export const useAutomationOptions = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const [options, setOptions] = useState<AutomationOptions>(emptyOptions);

  const groupsApi = useGroupsApi();
  const getCustomFields = useGetContactCustomFieldsApi();

  const loadOptions = useCallback(async () => {
    if (!currentCompanyId) return;

    setOptions(prev => ({ ...prev, isOptionsLoading: true }));

    const [groups, customFields] = await Promise.allSettled([
      groupsApi.getGroups({
        companyId: currentCompanyId,
        limit: 200,
        offset: 0,
        sort: 'name_asc',
        statuses: 'active',
        types: 'default,custom,dynamic',
      }),
      getCustomFields({ companyId: currentCompanyId, sort: 'name_asc' }),
    ]);

    setOptions({
      groupOptions:
        groups.status === 'fulfilled'
          ? groups.value.data.items.map(g => ({ value: g.id, title: g.name }))
          : [],
      // Real contact custom fields. Option value is the metadata guid sent as-is.
      // entity carries the field type so the form can render the matching value input.
      metadataOptions:
        customFields.status === 'fulfilled'
          ? customFields.value.data.items
              .filter((f): f is typeof f & { metadataId: string } => f.metadataId != null)
              .map(f => ({ value: f.metadataId, title: f.name, entity: f.type }))
          : [],
      isOptionsLoading: false,
    });
  }, [currentCompanyId, groupsApi, getCustomFields]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  return { options, loadOptions };
};
